import { AutoAwesome } from "@mui/icons-material";
import { Alert, Button, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import { useCallback, useState } from "react";
import { useBeforeUnload, useBlocker, useParams } from "react-router-dom";
import { ConfirmationModal } from "src/components/ConfirmationModal";
import { DocViewerMoreOptions } from "src/components/DocViewer/components/docViewerTopBar/actions/DocViewerMoreOptions";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { PageHeader } from "src/components/PageHeader/PageHeader";
import { getIncompleteQualityEventFieldPaths } from "src/components/QualityEvents/quality-event-conditions";
import {
  canEditQualityEventRecord,
  getQualityEventDraft,
  isQualityEventStepUnlocked,
} from "src/components/QualityEvents/quality-event-draft";
import { QualityEventActivityTable } from "src/components/QualityEvents/QualityEventActivityTable";
import { QualityEventDraftForm } from "src/components/QualityEvents/QualityEventDraftForm";
import { QualityEventFieldValue } from "src/components/QualityEvents/QualityEventFieldValue";
import { useOpenQualityEventAgent } from "src/components/QualityEvents/useOpenQualityEventAgent";
import { QualityEventRecordSkeleton } from "src/components/Skeletons/QualityEventRecordSkeleton";
import { useSnackbar } from "src/components/Snackbar";
import { ActivityTableSkeleton } from "src/components/ui/ActivityTableSkeleton";
import { AiGradientButton } from "src/components/ui/AiGradientButton";
import { StepAccordion } from "src/components/ui/StepAccordion";
import { StepAccordionGroup } from "src/components/ui/StepAccordionGroup";
import { useGetUser, useGetUsersByOrg } from "src/hooks/queries";
import {
  usePrepareQualityEventDraft,
  useSaveQualityEventRecord,
} from "src/hooks/queries/quality-event-mutations";
import {
  useGetQualityEventAudit,
  useGetQualityEventFiles,
  useGetQualityEventRecord,
} from "src/hooks/queries/quality-events";
import { qualityEventErrorMessage } from "src/services/quality-events";
import {
  isQualityEventApproval,
  QualityEventDraft,
  QualityEventValue,
} from "src/types/quality-events";
import { getUserDisplayName } from "src/utils/user";

const formatSummaryDate = (date: string) =>
  new Date(date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const QualityEventRecordPage = () => {
  const { orgId = "", qualityEventKey = "", recordId = "" } = useParams();
  const openAgent = useOpenQualityEventAgent();
  const recordQuery = useGetQualityEventRecord({ orgId, recordId });
  const users = useGetUsersByOrg({ orgId });
  const files = useGetQualityEventFiles({ orgId });
  const audit = useGetQualityEventAudit({ orgId, recordId });
  const { data: currentUser } = useGetUser();
  const { showSnackbar } = useSnackbar();
  const save = useSaveQualityEventRecord({ orgId });
  const prepareDraft = usePrepareQualityEventDraft({ orgId });
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>(
    {}
  );
  const [editingStep, setEditingStep] = useState<{
    key: string;
    draft: QualityEventDraft;
  } | null>(null);
  const editingDefinition = recordQuery.data?.definition.definition.steps.find(
    (step) => editingStep?.key === `${recordId}:${step.key}`
  );
  const dirtyDraft =
    !!editingStep &&
    !!editingDefinition &&
    !isEqual(
      editingStep.draft,
      getQualityEventDraft({
        record: recordQuery.data,
        step: editingDefinition,
      })
    );
  const blocker = useBlocker(dirtyDraft);
  useBeforeUnload(
    useCallback(
      (event) => {
        if (!dirtyDraft) return;
        event.preventDefault();
        event.returnValue = "";
      },
      [dirtyDraft]
    )
  );
  if (recordQuery.isLoading || users.isLoading)
    return <QualityEventRecordSkeleton />;
  if (
    recordQuery.data?.qualityEventKey !== qualityEventKey ||
    !users.data ||
    users.isError
  )
    return (
      <Alert severity="error">
        Unable to load this record. Return to Quality Events and try again.
      </Alert>
    );

  const record = recordQuery.data;
  const orgUsers = users.data;
  const steps = record.definition.definition.steps;
  const canEdit =
    canEditQualityEventRecord(record, currentUser) &&
    steps.some((step) => isQualityEventStepUnlocked(record, step.key));
  const summaryFields = steps.flatMap((step) =>
    step.fields
      .filter(
        (field) =>
          field.showInOverview &&
          field.type !== "approval" &&
          field.type !== "repeatable"
      )
      .map((field) => ({
        key: `${step.key}:${field.key}`,
        label: field.label,
        value: (
          <QualityEventFieldValue
            field={field}
            value={
              record.steps[step.key]?.answers[field.key] as
                | QualityEventValue
                | undefined
            }
            files={files.data ?? []}
            orgId={orgId}
            users={orgUsers}
          />
        ),
      }))
  );
  const qualityEventId = qualityEventKey.toUpperCase();
  const currentStepTitle = steps.find(
    (step) => step.key === record.currentStepKey
  )?.title;
  const workflowAnswers = Object.assign(
    {},
    ...Object.values(record.steps).map((step) => step.answers)
  );
  const workflowAnswersByStep = Object.fromEntries(
    Object.entries(record.steps).map(([key, saved]) => [key, saved.answers])
  );
  const saveStep = (stepKey: string, draft: QualityEventDraft) =>
    prepareDraft.mutate(draft, {
      onSuccess: (prepared) =>
        save.mutate(
          {
            recordId,
            body: {
              expectedVersion: draft.expectedVersion ?? record.version,
              stepKey,
              ...(stepKey === steps[0].key && { title: prepared.title }),
              answers: prepared.answers,
            },
          },
          {
            onSuccess: () => {
              setEditingStep((current) =>
                current?.key === `${recordId}:${stepKey}` ? null : current
              );
            },
            onError: (error) => showSnackbar(qualityEventErrorMessage(error)),
          }
        ),
      onError: (error) => showSnackbar(qualityEventErrorMessage(error)),
    });
  const summaryItems = [
    {
      key: "id",
      label: `${qualityEventId} ID`,
      value: `${qualityEventId}-${recordId.slice(0, 8).toUpperCase()}`,
    },
    {
      key: "status",
      label: "Status",
      value: { OPEN: "Open", IN_REVIEW: "In review", CLOSED: "Closed" }[
        record.status
      ],
    },
    {
      key: "owner",
      label: "Created by",
      value: getUserDisplayName(record.createdBy),
    },
    {
      key: "opened",
      label: "Opened",
      value: formatSummaryDate(record.createdAt),
    },
    ...(record.closedAt
      ? [
          {
            key: "closed",
            label: "Closed",
            value: formatSummaryDate(record.closedAt),
          },
        ]
      : []),
    {
      key: "current-step",
      label: "Current step",
      value:
        record.status === "CLOSED"
          ? "Closed"
          : (currentStepTitle ?? "Not provided"),
    },
    ...summaryFields,
  ];
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb
            items={[
              {
                label: "Quality Event Management",
                to: `/${orgId}/quality-events`,
              },
              {
                label: record.definition.name,
                to: `/${orgId}/quality-events/${qualityEventKey}`,
              },
              {
                label: record.title || `Untitled ${record.definition.name}`,
              },
            ]}
          />
        }
        rightButtons={
          canEdit && (
            <AiGradientButton
              startIcon={<AutoAwesome />}
              disabled={dirtyDraft || save.isPending || prepareDraft.isPending}
              onClick={() => openAgent(record.definition, record)}
            >
              Edit
            </AiGradientButton>
          )
        }
      />
      <div className="flex flex-col gap-4 px-3 py-2">
        <section aria-label={`${record.definition.name} summary`}>
          <dl className="m-0 grid grid-cols-1 gap-4 min-[900px]:grid-cols-4">
            {summaryItems.map(({ key, label, value }) => (
              <div key={key} className="min-w-0">
                <Typography
                  component="dt"
                  variant="overline"
                  color="text.secondary"
                  className="leading-[1.2]"
                >
                  {label}
                </Typography>
                <Typography
                  component="dd"
                  variant="body2"
                  className="m-0 mt-1 break-words [&_.MuiTypography-root]:[font:inherit]"
                >
                  {value}
                </Typography>
              </div>
            ))}
          </dl>
        </section>
        <section aria-label="Workflow steps" className="flex flex-col gap-2">
          <Typography
            variant="subtitle1"
            component="h2"
            className="font-semibold"
          >
            Workflow steps
          </Typography>
          <StepAccordionGroup>
            {steps.map((step, index) => {
              const savedStep = record.steps[step.key];
              const completed = !!savedStep?.completedAt;
              const current =
                record.currentStepKey === step.key &&
                record.status !== "CLOSED";
              const accordionKey = `${recordId}:${step.key}`;
              const draft =
                editingStep?.key === accordionKey
                  ? editingStep.draft
                  : getQualityEventDraft({ record, step });
              const editing = editingStep?.key === accordionKey;
              const unchanged =
                editing &&
                isEqual(draft, getQualityEventDraft({ record, step }));
              const invalidRequirementPaths =
                current && savedStep?.updatedAt
                  ? Array.from(
                      getIncompleteQualityEventFieldPaths({
                        fields: step.fields,
                        values: savedStep.answers,
                        allAnswers: workflowAnswers,
                        sourceAnswersByStep: workflowAnswersByStep,
                        completionRequirements: step.completionRequirements,
                        files: {},
                        requiredFieldPaths: new Set(),
                        validateAllRequirements: true,
                      })
                    )
                  : [];
              const waitingForApproval =
                current &&
                Object.values(savedStep?.answers ?? {}).some(
                  (answer) =>
                    isQualityEventApproval(answer) &&
                    answer.status === "PENDING"
                );
              return (
                <StepAccordion
                  key={accordionKey}
                  stepNumber={index + 1}
                  title={step.title}
                  status={
                    completed ? "complete" : current ? "current" : "locked"
                  }
                  missingRequirementCount={invalidRequirementPaths.length}
                  waitingForApproval={waitingForApproval}
                  actions={
                    expandedSteps[accordionKey] &&
                    canEdit &&
                    isQualityEventStepUnlocked(record, step.key) && (
                      <>
                        <AiGradientButton
                          startIcon={<AutoAwesome />}
                          disabled={
                            dirtyDraft ||
                            save.isPending ||
                            prepareDraft.isPending
                          }
                          onClick={() => openAgent(record.definition, record)}
                        >
                          Edit
                        </AiGradientButton>
                        <DocViewerMoreOptions
                          dataTestId={`record-${recordId}-${step.key}-options`}
                          menuId={`record-${recordId}-${step.key}-menu`}
                          triggerAriaLabel={`${step.title} options`}
                          triggerTooltipTitle="More options"
                          isDisabled={
                            save.isPending ||
                            prepareDraft.isPending ||
                            dirtyDraft
                          }
                          normalButtons={[
                            {
                              title: "Edit this step",
                              onClick: () => {
                                setEditingStep({
                                  key: accordionKey,
                                  draft: getQualityEventDraft({ record, step }),
                                });
                              },
                            },
                          ]}
                        />
                      </>
                    )
                  }
                  expanded={expandedSteps[accordionKey] ?? false}
                  onExpandedChange={(nextExpanded) =>
                    setExpandedSteps((previous) => ({
                      ...previous,
                      [accordionKey]: nextExpanded,
                    }))
                  }
                  detailsLabel={`${step.title} fields`}
                >
                  <QualityEventDraftForm
                    orgId={orgId}
                    record={record}
                    users={orgUsers}
                    step={step}
                    draft={draft}
                    invalidRequirementPaths={invalidRequirementPaths}
                    formId={`record-${recordId}-${step.key}`}
                    existingFiles={files.data ?? []}
                    userId={currentUser?.id ?? ""}
                    currentUser={currentUser}
                    pending={save.isPending || prepareDraft.isPending}
                    error={null}
                    onDraftChange={
                      editing
                        ? (nextDraft) =>
                            setEditingStep({
                              key: accordionKey,
                              draft: nextDraft,
                            })
                        : undefined
                    }
                    onApprovalDecision={async (fieldKey, decision) => {
                      try {
                        await save.mutateAsync({
                          recordId,
                          body: {
                            expectedVersion: record.version,
                            stepKey: step.key,
                            answers: { [fieldKey]: decision },
                          },
                        });
                      } catch (error) {
                        showSnackbar(qualityEventErrorMessage(error));
                      }
                    }}
                  />
                  {editing && (
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => setEditingStep(null)}
                        disabled={save.isPending || prepareDraft.isPending}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => saveStep(step.key, draft)}
                        disabled={
                          unchanged || save.isPending || prepareDraft.isPending
                        }
                      >
                        Save
                      </Button>
                    </div>
                  )}
                </StepAccordion>
              );
            })}
          </StepAccordionGroup>
        </section>
        <section aria-label="Activity history" className="flex flex-col gap-2">
          <Typography
            variant="subtitle1"
            component="h2"
            className="font-semibold"
          >
            Activity history
          </Typography>
          {audit.isLoading ? (
            <ActivityTableSkeleton />
          ) : audit.isError ? (
            <Alert severity="error">Unable to load activity history.</Alert>
          ) : (
            <QualityEventActivityTable entries={audit.data ?? []} />
          )}
        </section>
      </div>
      <ConfirmationModal
        open={blocker.state === "blocked"}
        title="Discard unsaved changes"
        content="Discard your unsaved step changes?"
        confirmText="Discard"
        onAbort={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
    </div>
  );
};
